import React, { useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { FieldArray } from "formik";
import { type FormikFormChildProps } from "../../FormikForm/FormikFormWrapperForJetpackConfig";
import { MarketingAutomationConfigKeys } from "kadow-jetpack-model";
import KC_Table from "../../../../../designsystem/KC_Table/KC_Table";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import KC_Button from "../../../../../designsystem/KC_Button/KC_Button";
import { getDescriptorForKey } from "../helpers/getDescriptorForKey";
import {
  CUSTOMER_OF_THE_MONTH_SCENARIO,
  DefaultScenariosRow,
  renderDefaultScenarios
} from "../helpers/renderDefaultScenarios";
import EmailTemplate, {
  EmailTemplateKeyType
} from "../../../../../components/modal/EmailTemplates/EmailTemplate";
import { tt } from "../../../../../utils/i18n/i18n";
import { useJetpackConfigSelector } from "../../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import shouldDisplayKey from "./shouldDisplayKey";

const JetpackMarketingAutomationV2: React.FC<FormikFormChildProps> = (
  props: FormikFormChildProps
) => {
  const config = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const hasCustomerOfTheMonth = useMemo(() => {
    return (
      accountToDisplay?.saasFeatures?.ltycm && config?.lty?.leaderboardEnabled
    );
  }, [accountToDisplay, config]);

  const handleToggle = (
    maKey: string,
    setFieldValue: (field: string, value: any) => void,
    value: boolean
  ) => {
    const tokenCount = value ? 1 : 0;
    setFieldValue(`ma.${maKey}`, tokenCount);
  };

  const openEmailTemplate = (maKey: EmailTemplateKeyType) => {
    void NiceModal.show(EmailTemplate, {
      maKey
    });
  };

  return (
    <div className="px-4" id="config-marketing-automation">
      <FieldArray name="ma">
        {() => (
          <>
            <KC_Table
              keyPrefix="synthesis-winners"
              className="mt-4"
              headers={
                <>
                  <th>
                    <KC_UIText
                      weight={KC_Weights.reg}
                      text={tt("follow_up_scenario")}
                      className={"text-secondary"}
                    />
                  </th>
                  <th className="d-none d-lg-table-cell">
                    <KC_UIText
                      weight={KC_Weights.reg}
                      text={tt("trigger")}
                      className="text-secondary"
                    />
                  </th>
                  <th className="text-center d-none d-lg-table-cell">
                    <KC_UIText
                      weight={KC_Weights.reg}
                      text={tt("email_content")}
                      className={"text-secondary"}
                    />
                  </th>
                </>
              }
            >
              <>
                {renderDefaultScenarios(
                  config,
                  accountToDisplay,
                  openEmailTemplate
                )}
                {Object.values(MarketingAutomationConfigKeys).map(
                  (maKey, idx) => {
                    const mustDisplay = shouldDisplayKey(
                      accountToDisplay,
                      maKey
                    );

                    if (!mustDisplay) {
                      return null;
                    }

                    const descriptor = getDescriptorForKey(maKey);

                    if (descriptor == null) {
                      return null;
                    }

                    return (
                      <tr key={descriptor.key}>
                        <td>
                          <div className="w-100 d-flex gap-sm justify-content-center align-items-center">
                            <KC_Toggle
                              id={descriptor.key}
                              isChecked={
                                !!props.config.ma?.[maKey] &&
                                props.config.ma[maKey] > 0
                              }
                              onToggle={() => {
                                handleToggle(
                                  maKey,
                                  props.setFieldValue,
                                  !props.config.ma?.[maKey] ||
                                    props.config.ma[maKey] === 0
                                );
                              }}
                            />
                            <KC_UIText
                              weight={KC_Weights.reg}
                              text={descriptor.name}
                            />
                          </div>
                        </td>
                        <td className="d-none d-lg-table-cell">
                          <KC_UIText
                            weight={KC_Weights.reg}
                            text={descriptor.description}
                          />
                        </td>
                        <td className="text-center d-none d-lg-table-cell">
                          <KC_Button
                            isTransparent={true}
                            text={tt("view")}
                            onClick={() => openEmailTemplate(maKey)}
                            className="text-semi-bold d-flex"
                          />
                        </td>
                      </tr>
                    );
                  }
                )}
                {!!hasCustomerOfTheMonth && (
                  <DefaultScenariosRow
                    scenario={CUSTOMER_OF_THE_MONTH_SCENARIO}
                    key="ltycm"
                    onClick={(maKey) => openEmailTemplate(maKey)}
                  />
                )}
              </>
            </KC_Table>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default JetpackMarketingAutomationV2;
